import { authHeaders, throwOnError } from "./authHeaders";

/**
 * Client for /api/alerts/email-preferences — self-serve alert email control.
 *
 * Semantics mirrored from the API (and api/compliance/notify.ts):
 *   - Once a hospital has any preference row, preferences are authoritative
 *     there. The all-supervisors fallback applies only where nobody has ever
 *     saved a preference.
 *   - The officer-activity summary is strictly opt-in and never falls back.
 *   - Opting out deactivates the row rather than deleting it, so "opted out"
 *     stays distinguishable from "never asked".
 */

export interface EmailPreference {
  id: string;
  hospital_id: string;
  hospital_name: string | null;
  email: string;
  name: string | null;
  notify_overdue: boolean;
  notify_daily: boolean;
  notify_officer_summary: boolean;
  active: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

export interface EmailPreferencesResponse {
  email: string;
  is_admin: boolean;
  hospitals: Array<{ id: string; name: string }>;
  /** The caller's own rows. A hospital with no row = no preference saved yet. */
  preferences: EmailPreference[];
  /** Present only when requested with all=1 by an admin. */
  all_recipients?: EmailPreference[];
}

export async function fetchEmailPreferences(all = false): Promise<EmailPreferencesResponse> {
  const response = await fetch(`/api/alerts/email-preferences${all ? "?all=1" : ""}`, {
    headers: authHeaders(),
  });
  await throwOnError(response, "Failed to load email preferences");
  return response.json();
}

export interface EmailPreferenceInput {
  hospital_id: string;
  notify_overdue?: boolean;
  notify_daily?: boolean;
  notify_officer_summary?: boolean;
  active?: boolean;
  /** Admin override only: manage another person's row. */
  email?: string;
  name?: string;
}

export async function saveEmailPreference(input: EmailPreferenceInput): Promise<EmailPreference> {
  const response = await fetch("/api/alerts/email-preferences", {
    method: "PUT",
    headers: authHeaders(true),
    body: JSON.stringify(input),
  });
  await throwOnError(response, "Failed to save email preference");
  const body = await response.json();
  return body.preference;
}
